/**
 * DoserneTimesheet — personal timesheet (Pontaj) for the WEB / PWA.
 *
 * Full parity with the mobile timesheet tab, adapted for browser/desktop:
 *  - Weekly day strip + day selection, overtime counter (40h), red-day banner
 *  - Add / edit / delete work entries (Project · Activity · From–To · Comment)
 *  - Kinds: work / vacation / sick
 *  - Auto lunch-break card + paid-break code apply/remove
 *  - Vacation (Ferie) requests with balance + admin approvals
 *  - Monthly summary + in-app notifications
 *  - Admin week-lock + auto-lock controls
 *  - RO / NO / EN localisation (self-contained dictionary, mirrors mobile)
 *
 * Uses ONLY the shared, platform-agnostic backend under /api/doserne/timesheet.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, ChevronLeft, ChevronRight, Bell, Sun, BarChart3, Lock, Unlock, Clock,
  Coffee, Trash2, Pencil, Copy, X, Check, Loader2, AlertCircle, Flame, Hourglass,
  Briefcase, Search, History, Stethoscope, CheckCircle2, XCircle, Info,
} from "lucide-react";
import { useDoserneAuth } from "./doserneAuth";
import { useLang } from "./i18n";
import DoserneShell from "./DoserneShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DICT = {
  ro: {
    title: "Pontaj", subtitle: "Orele tale de lucru", day: "Zi", add: "Adaugă ore", edit: "Editează", del: "Șterge",
    copy_next: "Copiază în ziua următoare", locked: "Zi blocată. Contactează administratorul dacă ai uitat ore.",
    project: "Proiect", pick_project: "Alege proiect…", activity: "Activitate", pick_activity: "Alege activitate…",
    from: "De la", to: "Până la", comment: "Comentariu", bad_time: "Oră invalidă",
    save: "Salvează", cancel: "Anulează", empty: "Nicio oră în această zi. Apasă „Adaugă ore”.",
    need_ot: "Îți mai trebuie {h} până la overtime", ot_active: "Overtime: +{h}", reached: "37,5 t atinse ✔",
    copied: "Ore copiate în ziua următoare", red: "Zi roșie — orele intră direct ca overtime",
    break_deducted: "Pauză −0,5 t (dedusă automat)", break_paid: "Pauză plătită", have_code: "Am cod pauză plătită",
    enter_code: "Cod pauză plătită", apply: "Aplică", remove_code: "Elimină cod", invalid_code: "Cod invalid",
    avail_codes: "Coduri valabile azi pentru proiectele tale:",
    kind_work: "Lucru", kind_vacation: "Concediu", kind_sick: "Boală",
    lock_week: "Blochează săptămâna", week_locked: "Săptămână blocată", autolock: "Auto-blocare după 7 zile",
    lock_global: "Admin · valabil pentru toți angajații", weekly_hours: "Ore normale / săptămână (overtime după)",
    monthly: "Sumar lunar", normal_lbl: "Ore normale", total_lbl: "Total lucrat", days_lbl: "zile", overtime: "Overtime",
    notifications: "Notificări", no_notifs: "Nicio notificare",
    ferie_title: "Concediu (Ferie)", balance_left: "zile rămase anul acesta", from_date: "De la data", to_date: "Până la data",
    send_request: "Trimite cererea", my_requests: "Cererile mele", to_approve: "De aprobat", approve: "Aprobă", reject: "Respinge",
    st_pending: "În așteptare", st_approved: "Aprobat", st_rejected: "Respins", no_requests: "Nicio cerere",
    min_month_note: "Minim o lună înainte", reject_reason: "Motivul respingerii", recent: "Recente",
    week: "Săptămâna", today: "Azi", prev_week: "Săpt. anterioară", next_week: "Săpt. următoare", loading: "Se încarcă…",
  },
  no: {
    title: "Timeliste", subtitle: "Dine arbeidstimer", day: "Dag", add: "Legg til timer", edit: "Rediger", del: "Slett",
    copy_next: "Kopier til neste dag", locked: "Dagen er låst. Kontakt administrator hvis du glemte timer.",
    project: "Prosjekt", pick_project: "Velg prosjekt…", activity: "Aktivitet", pick_activity: "Velg aktivitet…",
    from: "Fra", to: "Til", comment: "Kommentar", bad_time: "Ugyldig tid",
    save: "Lagre", cancel: "Avbryt", empty: "Ingen timer denne dagen. Trykk «Legg til timer».",
    need_ot: "{h} igjen til overtid", ot_active: "Overtid: +{h}", reached: "37,5 t nådd ✔",
    copied: "Timer kopiert til neste dag", red: "Rød dag — timer teller som overtid",
    break_deducted: "Pause −0,5 t (trukket)", break_paid: "Pause betalt", have_code: "Jeg har kode for betalt pause",
    enter_code: "Kode for betalt pause", apply: "Bruk", remove_code: "Fjern kode", invalid_code: "Ugyldig kode",
    avail_codes: "Gyldige koder i dag for dine prosjekter:",
    kind_work: "Arbeid", kind_vacation: "Ferie", kind_sick: "Syk",
    lock_week: "Lås uken", week_locked: "Uken er låst", autolock: "Auto-lås etter 7 dager",
    lock_global: "Admin · gjelder alle ansatte", weekly_hours: "Normaltimer / uke (overtid etter)",
    monthly: "Månedssammendrag", normal_lbl: "Normale timer", total_lbl: "Totalt arbeidet", days_lbl: "dager", overtime: "Overtid",
    notifications: "Varsler", no_notifs: "Ingen varsler",
    ferie_title: "Ferie", balance_left: "dager igjen i år", from_date: "Fra dato", to_date: "Til dato",
    send_request: "Send søknad", my_requests: "Mine søknader", to_approve: "Til godkjenning", approve: "Godkjenn", reject: "Avslå",
    st_pending: "Venter", st_approved: "Godkjent", st_rejected: "Avslått", no_requests: "Ingen søknader",
    min_month_note: "Minst én måned i forveien", reject_reason: "Årsak til avslag", recent: "Nylige",
    week: "Uke", today: "I dag", prev_week: "Forrige uke", next_week: "Neste uke", loading: "Laster…",
  },
  en: {
    title: "Timesheet", subtitle: "Your working hours", day: "Day", add: "Add hours", edit: "Edit", del: "Delete",
    copy_next: "Copy to next day", locked: "Day locked. Contact admin if you forgot hours.",
    project: "Project", pick_project: "Pick project…", activity: "Activity", pick_activity: "Pick activity…",
    from: "From", to: "To", comment: "Comment", bad_time: "Invalid time",
    save: "Save", cancel: "Cancel", empty: "No hours this day. Click “Add hours”.",
    need_ot: "{h} left until overtime", ot_active: "Overtime: +{h}", reached: "37.5 h reached ✔",
    copied: "Hours copied to next day", red: "Red day — hours count as overtime",
    break_deducted: "Break −0.5h (auto-deducted)", break_paid: "Break paid", have_code: "I have a paid-break code",
    enter_code: "Paid-break code", apply: "Apply", remove_code: "Remove code", invalid_code: "Invalid code",
    avail_codes: "Codes valid today for your projects:",
    kind_work: "Work", kind_vacation: "Vacation", kind_sick: "Sick",
    lock_week: "Lock week", week_locked: "Week locked", autolock: "Auto-lock after 7 days",
    lock_global: "Admin · applies to all employees", weekly_hours: "Normal hours / week (overtime after)",
    monthly: "Monthly summary", normal_lbl: "Normal hours", total_lbl: "Total worked", days_lbl: "days", overtime: "Overtime",
    notifications: "Notifications", no_notifs: "No notifications",
    ferie_title: "Vacation", balance_left: "days left this year", from_date: "From date", to_date: "To date",
    send_request: "Send request", my_requests: "My requests", to_approve: "To approve", approve: "Approve", reject: "Reject",
    st_pending: "Pending", st_approved: "Approved", st_rejected: "Rejected", no_requests: "No requests",
    min_month_note: "At least one month ahead", reject_reason: "Rejection reason", recent: "Recent",
    week: "Week", today: "Today", prev_week: "Previous week", next_week: "Next week", loading: "Loading…",
  },
};

const WD = ["Ma", "Ti", "On", "To", "Fr", "Lø", "Sø"];
const MONTHS_L = {
  ro: ["ian", "feb", "mar", "apr", "mai", "iun", "iul", "aug", "sep", "oct", "noi", "dec"],
  no: ["jan", "feb", "mar", "apr", "mai", "jun", "jul", "aug", "sep", "okt", "nov", "des"],
  en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
};

const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const mondayOf = (d) => { const x = new Date(d); const dow = (x.getDay() + 6) % 7; x.setDate(x.getDate() - dow); return x; };
const addDays = (isoStr, n) => { const d = new Date(`${isoStr}T00:00:00`); d.setDate(d.getDate() + n); return iso(d); };
const shiftMonth = (base, n) => { const [y, m] = base.split("-").map(Number); const d = new Date(y, m - 1 + n, 1); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`; };
const hoursLabel = (min) => `${((min || 0) / 60).toFixed(1).replace(".", ",")} t`;
const fmtDay = (isoStr, lang) => { const m = (MONTHS_L[lang] || MONTHS_L.ro); return `${Number(isoStr.slice(8, 10))}. ${m[Number(isoStr.slice(5, 7)) - 1]}`; };
const addMin = (hhmm, mins) => {
  const [h, m] = (hhmm || "00:00").split(":").map(Number);
  const t = Math.min((h || 0) * 60 + (m || 0) + mins, 23 * 60 + 55);
  return `${pad(Math.floor(t / 60))}:${pad(t % 60)}`;
};
const RE_TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

const RECENT_PROJ = "ts_web_recent_projects";
const RECENT_ACT = "ts_web_recent_activities";
const readRecent = (key) => { try { const raw = localStorage.getItem(key); return raw ? raw.split(",").filter(Boolean) : []; } catch { return []; } };
const pushRecent = (key, id) => {
  if (!id) return;
  try {
    const prev = readRecent(key);
    const next = [id, ...prev.filter((x) => x !== id)].slice(0, 5);
    localStorage.setItem(key, next.join(","));
  } catch { /* quota */ }
};

// ---------------------------------------------------------------------------
// Searchable combobox (project / activity picker) with recent items.
// ---------------------------------------------------------------------------
function Combobox({ value, placeholder, options, recentIds, itemsById, onSelect, testid, L }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return options.filter((o) => !s || o.label.toLowerCase().includes(s));
  }, [q, options]);

  const recent = useMemo(
    () => (recentIds || []).map((id) => itemsById[id]).filter(Boolean),
    [recentIds, itemsById],
  );
  const showRecent = !q.trim() && recent.length > 0;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-left text-sm text-slate-900 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-cyan-200"
        data-testid={testid}
      >
        <span className={value ? "text-slate-900 truncate" : "text-slate-400 truncate"}>{value || placeholder}</span>
        <ChevronRight className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100">
            <Search className="w-4 h-4 text-slate-400" />
            <input
              autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="…"
              className="flex-1 text-sm outline-none bg-transparent text-slate-900"
              data-testid={`${testid}-search`}
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {showRecent && (
              <>
                <div className="px-3 pt-1.5 pb-1 text-[10px] font-black uppercase tracking-widest text-slate-400">{L.recent}</div>
                {recent.map((it) => (
                  <button
                    key={`r-${it.id}`} type="button"
                    onClick={() => { onSelect(it.id); setOpen(false); setQ(""); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-800 hover:bg-cyan-50 text-left"
                  >
                    <History className="w-3.5 h-3.5 text-cyan-500 flex-shrink-0" />
                    <span className="truncate">{it.label}</span>
                  </button>
                ))}
                <div className="my-1 border-t border-slate-100" />
              </>
            )}
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-slate-400">—</div>
            ) : filtered.map((o) => (
              <button
                key={o.id} type="button"
                onClick={() => { onSelect(o.id); setOpen(false); setQ(""); }}
                className={`w-full flex items-center px-3 py-2 text-sm hover:bg-cyan-50 text-left ${o.id === value?.__id ? "bg-cyan-50" : ""}`}
              >
                <span className="truncate text-slate-800">{o.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Entry modal — add/edit work OR request vacation/sick.
// ---------------------------------------------------------------------------
function EntryModal({ L, date, entry, projects, activities, defaultFrom, authHeader, onClose, onSaved }) {
  const [kind, setKind] = useState(entry?.kind || "work");
  const [projectId, setProjectId] = useState(entry?.project_id || "");
  const [activityId, setActivityId] = useState(entry?.activity_id || "");
  const [fromT, setFromT] = useState(entry?.from_time || defaultFrom || "07:00");
  const [toT, setToT] = useState(entry?.to_time || (defaultFrom ? addMin(defaultFrom, 60) : "15:00"));
  const [comment, setComment] = useState(entry?.comment || "");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [balance, setBalance] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [recentProj, setRecentProj] = useState(() => readRecent(RECENT_PROJ));
  const [recentAct, setRecentAct] = useState(() => readRecent(RECENT_ACT));

  const isWork = kind === "work";
  const isVacation = kind === "vacation";

  const projLabel = (p) => `${p.number ? p.number + " " : ""}${p.name}`.trim();
  const projOptions = useMemo(() => projects.map((p) => ({ id: p.id, label: projLabel(p) })), [projects]);
  const projById = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, { id: p.id, label: projLabel(p) }])), [projects]);
  const actOptions = useMemo(() => activities.map((a) => ({ id: a.id, label: a.name })), [activities]);
  const actById = useMemo(() => Object.fromEntries(activities.map((a) => [a.id, { id: a.id, label: a.name }])), [activities]);
  const selProjLabel = projById[projectId]?.label || "";
  const selActLabel = actById[activityId]?.label || "";

  useEffect(() => {
    if (isVacation && !balance) {
      axios.get(`${API}/doserne/timesheet/vacation/balance`, authHeader()).then((r) => setBalance(r.data)).catch(() => {});
    }
  }, [isVacation, balance, authHeader]);

  const save = async () => {
    setErr("");
    if (isVacation) {
      if (!startDate || !endDate) { setErr(`${L.from_date} / ${L.to_date}`); return; }
      setBusy(true);
      try {
        await axios.post(`${API}/doserne/timesheet/vacation/requests`, { start_date: startDate, end_date: endDate, comment }, authHeader());
        onSaved();
      } catch (e) { setErr(e?.response?.data?.detail || "Error"); }
      setBusy(false);
      return;
    }
    if (isWork && !projectId) { setErr(L.pick_project); return; }
    if (!RE_TIME.test(fromT) || !RE_TIME.test(toT)) { setErr(L.bad_time); return; }
    setBusy(true);
    const body = { date, from_time: fromT, to_time: toT, is_break: false, kind, project_id: projectId, activity_id: activityId, comment };
    try {
      if (entry) await axios.put(`${API}/doserne/timesheet/entries/${entry.id}`, body, authHeader());
      else await axios.post(`${API}/doserne/timesheet/entries`, body, authHeader());
      if (projectId) { pushRecent(RECENT_PROJ, projectId); }
      if (activityId) { pushRecent(RECENT_ACT, activityId); }
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || "Error"); }
    setBusy(false);
  };

  const KINDS = [
    { k: "work", label: L.kind_work, Icon: Briefcase },
    { k: "vacation", label: L.kind_vacation, Icon: Sun },
    { k: "sick", label: L.kind_sick, Icon: Stethoscope },
  ];

  return (
    <Overlay onClose={onClose}>
      <div className="p-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-black text-slate-900">{entry ? L.edit : L.add}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100" data-testid="ts-modal-close"><X className="w-5 h-5" /></button>
        </div>

        {!entry && (
          <div className="grid grid-cols-3 gap-2 mb-4">
            {KINDS.map(({ k, label, Icon }) => (
              <button
                key={k} type="button" onClick={() => setKind(k)}
                className={`flex items-center justify-center gap-1.5 py-2.5 rounded-xl border text-sm font-bold transition ${kind === k ? "bg-cyan-500 border-cyan-500 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}
                data-testid={`ts-kind-${k}`}
              >
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
          </div>
        )}

        {isVacation ? (
          <div className="space-y-4">
            <div className="flex items-start gap-2 bg-cyan-50 border border-cyan-100 rounded-xl px-3 py-2.5 text-sm text-cyan-800">
              <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{L.min_month_note}{balance ? ` · ${balance.remaining_days} ${L.balance_left}` : ""}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FieldLabel label={L.from_date}>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={INP} data-testid="ts-vac-from" />
              </FieldLabel>
              <FieldLabel label={L.to_date}>
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={INP} data-testid="ts-vac-to" />
              </FieldLabel>
            </div>
            <FieldLabel label={L.comment}>
              <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} className={INP} data-testid="ts-vac-comment" />
            </FieldLabel>
          </div>
        ) : (
          <div className="space-y-4">
            {isWork && (
              <>
                <FieldLabel label={L.project}>
                  <Combobox value={selProjLabel} placeholder={L.pick_project} options={projOptions} recentIds={recentProj} itemsById={projById}
                    onSelect={(id) => setProjectId(id)} testid="ts-project" L={L} />
                </FieldLabel>
                <FieldLabel label={L.activity}>
                  <Combobox value={selActLabel} placeholder={L.pick_activity} options={actOptions} recentIds={recentAct} itemsById={actById}
                    onSelect={(id) => setActivityId(id)} testid="ts-activity" L={L} />
                </FieldLabel>
              </>
            )}
            <div className="grid grid-cols-2 gap-3">
              <FieldLabel label={L.from}>
                <input type="time" value={fromT} onChange={(e) => setFromT(e.target.value)} step="300" className={INP} data-testid="ts-from" />
              </FieldLabel>
              <FieldLabel label={L.to}>
                <input type="time" value={toT} onChange={(e) => setToT(e.target.value)} step="300" className={INP} data-testid="ts-to" />
              </FieldLabel>
            </div>
            <FieldLabel label={L.comment}>
              <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} className={INP} data-testid="ts-comment" />
            </FieldLabel>
          </div>
        )}

        {err ? <div className="mt-3 text-sm font-semibold text-rose-600">{err}</div> : null}

        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-slate-100 text-slate-700 font-bold hover:bg-slate-200" data-testid="ts-cancel">{L.cancel}</button>
          <button onClick={save} disabled={busy} className="flex-[2] py-2.5 rounded-xl bg-cyan-500 text-white font-black hover:bg-cyan-600 disabled:opacity-60 flex items-center justify-center gap-2" data-testid="ts-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (isVacation ? L.send_request : L.save)}
          </button>
        </div>
      </div>
    </Overlay>
  );
}

const INP = "w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-900 outline-none focus:ring-2 focus:ring-cyan-200 focus:border-cyan-300";
function FieldLabel({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-1.5">{label}</div>
      {children}
    </label>
  );
}

function Overlay({ children, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" onMouseDown={onClose}>
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto" onMouseDown={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Paid-break code modal.
// ---------------------------------------------------------------------------
function BreakCodeModal({ L, date, authHeader, onClose, onApplied }) {
  const [code, setCode] = useState("");
  const [dayCodes, setDayCodes] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/doserne/timesheet/break-codes-for-day`, { ...authHeader(), params: { date } })
      .then((r) => setDayCodes(r.data.codes || [])).catch(() => {});
  }, [date, authHeader]);

  const apply = async () => {
    const c = code.trim().toUpperCase();
    if (!c) return;
    setBusy(true);
    try {
      await axios.post(`${API}/doserne/timesheet/break-code`, { date, code: c }, authHeader());
      onApplied();
    } catch (e) { toast.error(e?.response?.data?.detail || L.invalid_code); }
    setBusy(false);
  };

  return (
    <Overlay onClose={onClose}>
      <div className="p-5 sm:p-6">
        <h3 className="text-lg font-black text-slate-900 text-center mb-4">{L.enter_code}</h3>
        {dayCodes.length > 0 && (
          <div className="mb-4 space-y-1.5">
            <div className="text-[11px] font-bold text-slate-500">{L.avail_codes}</div>
            {dayCodes.map((c) => (
              <button key={c.code} type="button" onClick={() => setCode(c.code)}
                className="w-full flex items-center gap-3 bg-cyan-50 rounded-xl px-3 py-2 text-left hover:bg-cyan-100"
                data-testid={`ts-avail-code-${c.code}`}>
                <span className="text-base font-black tracking-widest text-cyan-700">{c.code}</span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-bold text-slate-800 truncate">{c.project_name}</span>
                  {c.label ? <span className="block text-xs text-cyan-700 truncate">🏷️ {c.label}</span> : null}
                </span>
                <ChevronRight className="w-4 h-4 text-cyan-500" />
              </button>
            ))}
          </div>
        )}
        <input
          value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={6} placeholder="XXXXXX"
          className="w-full h-14 rounded-xl border-2 border-slate-200 bg-slate-50 text-center text-2xl font-black tracking-[0.4em] text-slate-900 outline-none focus:border-cyan-400"
          data-testid="ts-code-input"
        />
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-slate-100 text-slate-700 font-bold hover:bg-slate-200">{L.cancel}</button>
          <button onClick={apply} disabled={busy} className="flex-[2] py-2.5 rounded-xl bg-cyan-500 text-white font-black hover:bg-cyan-600 disabled:opacity-60 flex items-center justify-center" data-testid="ts-code-apply">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : L.apply}
          </button>
        </div>
      </div>
    </Overlay>
  );
}

// ---------------------------------------------------------------------------
// Monthly summary modal.
// ---------------------------------------------------------------------------
function MonthModal({ L, lang, initialMonth, authHeader, onClose }) {
  const [monthStr, setMonthStr] = useState(initialMonth);
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    axios.get(`${API}/doserne/timesheet/month`, { ...authHeader(), params: { month: monthStr } }).then((r) => setData(r.data)).catch(() => setData(null));
  }, [monthStr, authHeader]);

  const Row = ({ Icon, color, label, value, sub, testid }) => (
    <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-3" data-testid={testid}>
      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + "22" }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-black text-slate-900">{label}</div>
        {sub ? <div className="text-xs text-slate-400">{sub}</div> : null}
      </div>
      <div className="text-base font-black" style={{ color }}>{value}</div>
    </div>
  );

  return (
    <Overlay onClose={onClose}>
      <div className="p-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-black text-slate-900">{L.monthly}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => setMonthStr((m) => shiftMonth(m, -1))} className="p-2 rounded-lg hover:bg-slate-100" data-testid="ts-month-prev"><ChevronLeft className="w-5 h-5 text-slate-700" /></button>
          <span className="text-base font-black text-cyan-700 capitalize">{(MONTHS_L[lang] || MONTHS_L.ro)[Number(monthStr.slice(5, 7)) - 1]} {monthStr.slice(0, 4)}</span>
          <button onClick={() => setMonthStr((m) => shiftMonth(m, 1))} className="p-2 rounded-lg hover:bg-slate-100" data-testid="ts-month-next"><ChevronRight className="w-5 h-5 text-slate-700" /></button>
        </div>
        {!data ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>
        ) : (
          <div className="space-y-2">
            <Row Icon={Briefcase} color="#0891b2" label={L.normal_lbl} value={hoursLabel(data.normal_minutes)} sub={`${data.work_days} ${L.days_lbl}`} testid="sum-normal" />
            <Row Icon={Flame} color="#c2410c" label={L.overtime} value={hoursLabel(data.overtime_minutes)} testid="sum-overtime" />
            <Row Icon={Sun} color="#d97706" label={L.kind_vacation} value={hoursLabel(data.vacation_minutes)} sub={`${data.vacation_days} ${L.days_lbl}`} testid="sum-vacation" />
            <Row Icon={Stethoscope} color="#dc2626" label={L.kind_sick} value={hoursLabel(data.sick_minutes)} sub={`${data.sick_days} ${L.days_lbl}`} testid="sum-sick" />
            <div className="flex items-center justify-between bg-slate-900 rounded-xl px-4 py-3 mt-1">
              <span className="text-sm font-black text-white">{L.total_lbl}</span>
              <span className="text-lg font-black text-white">{hoursLabel(data.total_worked_minutes)}</span>
            </div>
          </div>
        )}
      </div>
    </Overlay>
  );
}

// ---------------------------------------------------------------------------
// Vacation (Ferie) modal — balance + my requests + admin approvals.
// ---------------------------------------------------------------------------
function FerieModal({ L, isAdmin, authHeader, onClose }) {
  const [balance, setBalance] = useState(null);
  const [mine, setMine] = useState([]);
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(false);
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = useCallback(async () => {
    try { setBalance((await axios.get(`${API}/doserne/timesheet/vacation/balance`, authHeader())).data); } catch { /* silent */ }
    try { setMine((await axios.get(`${API}/doserne/timesheet/vacation/requests`, authHeader())).data.requests || []); } catch { /* silent */ }
    if (isAdmin) { try { setPending((await axios.get(`${API}/doserne/timesheet/vacation/admin/requests`, { ...authHeader(), params: { status: "pending" } })).data.requests || []); } catch { /* silent */ } }
  }, [isAdmin, authHeader]);
  useEffect(() => { load(); }, [load]);

  const approve = async (id) => { setBusy(true); try { await axios.post(`${API}/doserne/timesheet/vacation/requests/${id}/decision`, { approved: true }, authHeader()); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } setBusy(false); };
  const confirmReject = async () => {
    if (!rejectId || !rejectReason.trim()) return;
    setBusy(true);
    try { await axios.post(`${API}/doserne/timesheet/vacation/requests/${rejectId}/decision`, { approved: false, reason: rejectReason.trim() }, authHeader()); setRejectId(null); setRejectReason(""); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    setBusy(false);
  };
  const cancel = async (id) => { setBusy(true); try { await axios.delete(`${API}/doserne/timesheet/vacation/requests/${id}`, authHeader()); await load(); } catch { /* silent */ } setBusy(false); };

  const chip = (s) => {
    const map = { pending: ["#d97706", "#fef3c7", L.st_pending], approved: ["#059669", "#d1fae5", L.st_approved], rejected: ["#dc2626", "#fee2e2", L.st_rejected] };
    const [c, bg, t] = map[s] || map.pending;
    return <span className="text-[11px] font-black px-2.5 py-1 rounded-full" style={{ color: c, backgroundColor: bg }}>{t}</span>;
  };

  return (
    <Overlay onClose={onClose}>
      <div className="p-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-black text-slate-900">{L.ferie_title}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"><X className="w-5 h-5" /></button>
        </div>
        {balance && (
          <div className="text-center bg-white border border-slate-200 rounded-2xl py-4 mb-4">
            <div className="text-4xl font-black text-cyan-700">{balance.remaining_days}<span className="text-base font-bold text-slate-400"> / {balance.allowance}</span></div>
            <div className="text-sm font-semibold text-slate-500">{L.balance_left}</div>
            {balance.pending_days > 0 ? <div className="text-xs font-bold text-amber-600 mt-1">{balance.pending_days} {L.st_pending.toLowerCase()}</div> : null}
          </div>
        )}
        {isAdmin && pending.length > 0 && (
          <>
            <div className="text-xs font-black uppercase tracking-widest text-slate-500 mt-4 mb-2">{L.to_approve} ({pending.length})</div>
            {pending.map((r) => (
              <div key={r.id} className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl p-3 mb-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-black text-slate-900 truncate">{r.user_name || r.user_email}</div>
                  <div className="text-sm font-semibold text-slate-600">{r.start_date} → {r.end_date} · {r.days} {L.days_lbl}</div>
                  {r.comment ? <div className="text-xs text-slate-400 italic">{r.comment}</div> : null}
                </div>
                <button disabled={busy} onClick={() => { setRejectId(r.id); setRejectReason(""); }} className="w-9 h-9 rounded-lg bg-rose-50 flex items-center justify-center" data-testid={`vac-reject-${r.id}`}><XCircle className="w-4.5 h-4.5 text-rose-600" /></button>
                <button disabled={busy} onClick={() => approve(r.id)} className="w-9 h-9 rounded-lg bg-emerald-500 flex items-center justify-center" data-testid={`vac-approve-${r.id}`}><Check className="w-4.5 h-4.5 text-white" /></button>
              </div>
            ))}
          </>
        )}
        <div className="text-xs font-black uppercase tracking-widest text-slate-500 mt-4 mb-2">{L.my_requests}</div>
        {mine.length === 0 ? <div className="text-sm text-slate-400">{L.no_requests}</div> : mine.map((r) => (
          <div key={r.id} className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl p-3 mb-2">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-700">{r.start_date} → {r.end_date} · {r.days} {L.days_lbl}</div>
              {r.status === "rejected" && r.reason ? <div className="text-xs text-slate-400 italic">{r.reason}</div> : null}
            </div>
            {chip(r.status)}
            {r.status !== "rejected" ? <button onClick={() => cancel(r.id)} className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-lg" data-testid={`vac-cancel-${r.id}`}><Trash2 className="w-4 h-4" /></button> : null}
          </div>
        ))}
      </div>

      {rejectId && (
        <Overlay onClose={() => setRejectId(null)}>
          <div className="p-5">
            <h3 className="text-base font-black text-slate-900 mb-3 text-center">{L.reject_reason}</h3>
            <textarea autoFocus value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={3} className={INP} data-testid="vac-reject-reason" />
            <div className="flex gap-2 mt-4">
              <button onClick={() => setRejectId(null)} className="flex-1 py-2.5 rounded-xl bg-slate-100 text-slate-700 font-bold">{L.cancel}</button>
              <button onClick={confirmReject} disabled={!rejectReason.trim() || busy} className="flex-[2] py-2.5 rounded-xl bg-rose-500 text-white font-black disabled:opacity-50" data-testid="vac-reject-confirm">{L.reject}</button>
            </div>
          </div>
        </Overlay>
      )}
    </Overlay>
  );
}

// ---------------------------------------------------------------------------
// Notifications modal.
// ---------------------------------------------------------------------------
function NotifModal({ L, notifs, onClose }) {
  return (
    <Overlay onClose={onClose}>
      <div className="p-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-black text-slate-900">{L.notifications}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"><X className="w-5 h-5" /></button>
        </div>
        {notifs.length === 0 ? <div className="text-sm text-slate-400 text-center py-6">{L.no_notifs}</div> : (
          <div className="space-y-2">
            {notifs.map((n) => (
              <div key={n.id} className={`flex items-start gap-3 rounded-xl border p-3 ${n.read ? "bg-white border-slate-200" : "bg-cyan-50 border-cyan-200"}`} data-testid={`notif-${n.id}`}>
                {n.status === "approved" ? <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5" /> : n.status === "rejected" ? <XCircle className="w-5 h-5 text-rose-500 mt-0.5" /> : <Info className="w-5 h-5 text-cyan-500 mt-0.5" />}
                <div className="min-w-0">
                  <div className="text-sm font-black text-slate-900">{n.title}</div>
                  <div className="text-xs text-slate-500">{n.body}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Overlay>
  );
}

// ===========================================================================
// MAIN PAGE
// ===========================================================================
function TimesheetInner() {
  const { user, authHeader } = useDoserneAuth();
  const { lang } = useLang();
  const L = DICT[lang] || DICT.ro;
  const isAdmin = !!user?.is_admin;

  const [selected, setSelected] = useState(() => iso(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [activities, setActivities] = useState([]);
  const [modal, setModal] = useState(null); // {entry} | null
  const [codeOpen, setCodeOpen] = useState(false);
  const [monthOpen, setMonthOpen] = useState(false);
  const [ferieOpen, setFerieOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [lockInfo, setLockInfo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/doserne/projects`, { ...authHeader(), params: { limit: 500 } }).then((r) => setProjects(r.data.projects || [])).catch(() => {});
    axios.get(`${API}/underlag/activities`, authHeader()).then((r) => setActivities(r.data.activities || [])).catch(() => {});
  }, [authHeader]);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData((await axios.get(`${API}/doserne/timesheet/day`, { ...authHeader(), params: { date: selected } })).data); }
    catch { setData(null); }
    setLoading(false);
  }, [selected, authHeader]);
  useEffect(() => { load(); }, [load]);

  const loadNotifs = useCallback(async () => {
    try { const { data: d } = await axios.get(`${API}/doserne/timesheet/notifications`, authHeader()); setNotifs(d.items || []); setUnread(d.unread || 0); } catch { /* silent */ }
  }, [authHeader]);
  useEffect(() => { loadNotifs(); }, [loadNotifs]);

  const selMonday = useMemo(() => iso(mondayOf(new Date(`${selected}T00:00:00`))), [selected]);
  const loadLock = useCallback(async () => {
    if (!isAdmin) return;
    try { setLockInfo((await axios.get(`${API}/doserne/timesheet/lock-status`, { ...authHeader(), params: { monday: selMonday } })).data); } catch { /* silent */ }
  }, [isAdmin, selMonday, authHeader]);
  useEffect(() => { loadLock(); }, [loadLock]);

  const week = data?.week;
  const dayTotal = data?.day?.worked_minutes || 0;
  const remaining = week?.remaining_to_overtime_minutes ?? 0;
  const weekOt = week?.week_overtime_minutes ?? 0;
  const locked = !!data?.locked;

  const dayAutoBreak = data?.day?.auto_break_minutes || 0;
  const firstWorkEntryId = (data?.entries || []).find((e) => !e.is_break && (e.kind || "work") === "work")?.id;
  const lastEnd = useMemo(() => {
    const work = (data?.entries || []).filter((e) => !e.is_break && (e.kind || "work") === "work" && e.to_time);
    return work.reduce((mx, e) => (e.to_time > mx ? e.to_time : mx), "");
  }, [data]);

  const openNotifs = async () => {
    setNotifOpen(true);
    if (unread > 0) { try { await axios.post(`${API}/doserne/timesheet/notifications/read-all`, {}, authHeader()); } catch { /* silent */ } setUnread(0); loadNotifs(); }
  };

  const doDelete = async (entry) => {
    try { await axios.delete(`${API}/doserne/timesheet/entries/${entry.id}`, authHeader()); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const doCopy = async (sourceDate) => {
    try {
      await axios.post(`${API}/doserne/timesheet/copy-day`, { source_date: sourceDate, target_date: addDays(sourceDate, 1) }, authHeader());
      toast.success(L.copied);
      if (addDays(sourceDate, 1) === selected) load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const clearCode = async () => {
    try { await axios.delete(`${API}/doserne/timesheet/break-code`, { ...authHeader(), params: { date: selected } }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const toggleWeekLock = async () => {
    if (!lockInfo) return;
    const nowLocked = !lockInfo.manual_locked;
    setLockInfo({ ...lockInfo, manual_locked: nowLocked });
    try { await axios.post(`${API}/doserne/timesheet/admin/lock`, { week_monday: selMonday, locked: nowLocked }, authHeader()); load(); }
    catch { loadLock(); }
  };
  const toggleAutoLock = async () => {
    if (!lockInfo) return;
    const days = lockInfo.auto_lock_after_days ? null : 7;
    setLockInfo({ ...lockInfo, auto_lock_after_days: days });
    try { await axios.put(`${API}/doserne/timesheet/admin/settings`, { auto_lock_after_days: days }, authHeader()); load(); }
    catch { loadLock(); }
  };
  const setWeeklyHours = async (h) => {
    try { await axios.put(`${API}/doserne/timesheet/admin/settings`, { weekly_hours: h }, authHeader()); load(); }
    catch { /* ignore */ }
  };

  const weekDays = week?.days || Array.from({ length: 7 }, (_, i) => ({ date: addDays(selMonday, i), weekday: i, worked_minutes: 0, red: false }));
  const monLabel = new Date(`${selMonday}T00:00:00`);

  return (
    <div className="max-w-3xl mx-auto">
      {/* Toolbar */}
      <div className="flex items-center justify-end gap-2 mb-3">
        <button onClick={openNotifs} className="relative p-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50" data-testid="ts-notif-btn">
          <Bell className="w-5 h-5 text-slate-700" />
          {unread > 0 ? <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-black flex items-center justify-center">{unread > 9 ? "9+" : unread}</span> : null}
        </button>
        <button onClick={() => setFerieOpen(true)} className="p-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50" data-testid="ts-ferie-btn" title={L.ferie_title}><Sun className="w-5 h-5 text-amber-500" /></button>
        <button onClick={() => setMonthOpen(true)} className="p-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50" data-testid="ts-month-btn" title={L.monthly}><BarChart3 className="w-5 h-5 text-cyan-600" /></button>
      </div>

      {/* Week navigation header — prominent, touch-friendly (44px) */}
      <div className="flex items-center gap-2 mb-2">
        <button onClick={() => setSelected(addDays(selMonday, -7))} title={L.prev_week} aria-label={L.prev_week}
          className="flex items-center justify-center w-11 h-11 rounded-xl bg-white border border-slate-200 text-slate-700 hover:bg-cyan-50 hover:border-cyan-300 active:scale-95 transition" data-testid="ts-week-prev">
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button onClick={() => setSelected(iso(new Date()))}
          className="flex-1 h-11 rounded-xl bg-white border border-slate-200 hover:border-cyan-300 flex flex-col items-center justify-center leading-tight active:scale-[0.98] transition" data-testid="ts-week-label">
          <span className="text-sm font-black text-slate-900">{L.week} {getWeekNr(monLabel)}</span>
          <span className="text-[11px] font-semibold text-slate-500">{fmtDay(selMonday, lang)} – {fmtDay(addDays(selMonday, 6), lang)}</span>
        </button>
        <button onClick={() => setSelected(addDays(selMonday, 7))} title={L.next_week} aria-label={L.next_week}
          className="flex items-center justify-center w-11 h-11 rounded-xl bg-white border border-slate-200 text-slate-700 hover:bg-cyan-50 hover:border-cyan-300 active:scale-95 transition" data-testid="ts-week-next">
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

      {/* Week strip */}
      <div className="bg-white border border-slate-200 rounded-2xl px-3 py-3 mb-3">
        <div className="grid grid-cols-7 gap-1">
          {weekDays.map((d) => {
            const isSel = d.date === selected;
            const isToday = d.date === iso(new Date());
            return (
              <button key={d.date} onClick={() => setSelected(d.date)} className="flex flex-col items-center gap-1 py-1 rounded-lg hover:bg-slate-50" data-testid={`ts-day-${d.date}`}>
                <span className={`text-[10px] font-black ${d.red ? "text-rose-500" : "text-slate-400"}`}>{WD[d.weekday]}</span>
                <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-black ${isSel ? "bg-cyan-500 text-white" : isToday ? "ring-2 ring-cyan-300 text-slate-800" : d.red ? "text-rose-500" : "text-slate-800"}`}>{Number(d.date.slice(8, 10))}</span>
                <span className="text-[9px] font-bold text-slate-400">{d.worked_minutes ? (d.worked_minutes / 60).toFixed(1).replace(".", ",") : "0"}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-between mb-3 px-1">
        <div className="text-xs font-semibold text-slate-500">{L.day}: <span className="font-black text-slate-800">{hoursLabel(dayTotal)}</span></div>
        <button onClick={() => setSelected(iso(new Date()))} className="text-xs font-black text-cyan-600 hover:underline" data-testid="ts-today-btn">{L.today}</button>
      </div>

      {/* Overtime counter */}
      <div className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 mb-3 ${remaining > 0 ? "bg-cyan-50 border-cyan-200" : "bg-orange-50 border-orange-200"}`}>
        {remaining > 0 ? <Hourglass className="w-4 h-4 text-cyan-700" /> : <Flame className="w-4 h-4 text-orange-600" />}
        <span className={`text-sm font-black ${remaining > 0 ? "text-cyan-700" : "text-orange-600"}`}>
          {remaining > 0 ? L.need_ot.replace("{h}", hoursLabel(remaining)) : weekOt > 0 ? L.ot_active.replace("{h}", hoursLabel(weekOt)) : L.reached}
        </span>
      </div>

      {data?.day?.red ? (
        <div className="flex items-center gap-2 rounded-lg bg-orange-50 px-3 py-2 mb-3">
          <AlertCircle className="w-4 h-4 text-orange-600" /><span className="text-xs font-bold text-orange-700">{L.red}</span>
        </div>
      ) : null}

      {/* Admin lock bar */}
      {isAdmin && lockInfo && (
        <div className="bg-white border border-slate-200 rounded-2xl px-4 py-2 mb-3">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 pt-1">{L.lock_global}</div>
          <div className="flex items-center gap-2 py-2 border-b border-slate-100">
            {lockInfo.manual_locked ? <Lock className="w-4 h-4 text-amber-500" /> : <Unlock className="w-4 h-4 text-slate-400" />}
            <span className="flex-1 text-sm font-bold text-slate-700">{lockInfo.manual_locked ? L.week_locked : L.lock_week}</span>
            <Toggle on={lockInfo.manual_locked} onColor="#f59e0b" onClick={toggleWeekLock} testid="ts-lock-week" />
          </div>
          <div className="flex items-center gap-2 py-2">
            <Clock className="w-4 h-4 text-slate-400" />
            <span className="flex-1 text-sm font-bold text-slate-700">{L.autolock}</span>
            <Toggle on={!!lockInfo.auto_lock_after_days} onColor="#06b6d4" onClick={toggleAutoLock} testid="ts-autolock" />
          </div>
          <div className="flex items-center gap-2 py-2 border-t border-slate-100">
            <Hourglass className="w-4 h-4 text-slate-400" />
            <span className="flex-1 text-sm font-bold text-slate-700">{L.weekly_hours}</span>
            <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-0.5" data-testid="ts-weekly-hours">
              {[37.5, 40].map((h) => {
                const active = Math.abs((week?.threshold_minutes || 2250) / 60 - h) < 0.01;
                return (
                  <button
                    key={h}
                    onClick={() => setWeeklyHours(h)}
                    className={`px-3 py-1 rounded-md text-sm font-black transition ${active ? "bg-cyan-500 text-white shadow" : "text-slate-600 hover:bg-white"}`}
                    data-testid={`ts-weekly-hours-${h}`}
                  >
                    {h.toString().replace(".", ",")} t
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {locked && (
        <div className="flex items-center gap-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2.5 mb-3">
          <Lock className="w-4 h-4 text-amber-600" /><span className="text-sm font-semibold text-amber-800">{L.locked}</span>
        </div>
      )}

      {/* Entries */}
      {loading ? (
        <div className="py-14 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-cyan-500" /></div>
      ) : (
        <div className="space-y-2">
          {(data?.entries || []).length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12 text-slate-400">
              <Clock className="w-8 h-8" /><span className="text-sm text-center">{L.empty}</span>
            </div>
          ) : (data.entries || []).map((e) => {
            const isAbsence = (e.kind || "work") !== "work";
            const clickable = !locked && !e.is_break && !isAbsence;
            return (
              <div key={e.id}
                className={`group flex items-center gap-3 rounded-xl border p-3 ${e.is_break ? "bg-slate-50 border-dashed border-slate-200" : "bg-white border-slate-200"} ${clickable ? "hover:border-cyan-300 cursor-pointer" : ""}`}
                onClick={() => clickable && setModal({ entry: e })}
                data-testid={`ts-entry-${e.id}`}
              >
                <div className="flex flex-col items-center w-12 flex-shrink-0">
                  <span className="text-xs font-black text-slate-700">{e.from_time}</span>
                  <span className="w-px h-2.5 bg-slate-200 my-0.5" />
                  <span className="text-xs font-black text-slate-700">{e.to_time}</span>
                </div>
                <div className="flex-1 min-w-0">
                  {e.is_break ? (
                    <span className="text-sm font-semibold text-slate-500">☕ {L.break_paid}</span>
                  ) : isAbsence ? (
                    <span className="text-sm font-black text-slate-800">{e.kind === "vacation" ? `🏖️ ${L.kind_vacation}` : `🤒 ${L.kind_sick}`}{e.comment ? ` · ${e.comment}` : ""}</span>
                  ) : (
                    <>
                      <div className="text-sm font-black text-slate-900 truncate">{e.project_name || "—"}</div>
                      {e.activity_name ? <div className="text-xs text-slate-500 truncate">{e.activity_name}</div> : null}
                      {e.comment ? <div className="text-xs text-slate-400 italic truncate">{e.comment}</div> : null}
                    </>
                  )}
                </div>
                <div className="text-sm font-black text-cyan-700 flex-shrink-0">{hoursLabel(e.minutes - (e.id === firstWorkEntryId ? dayAutoBreak : 0))}</div>
                {!locked && !isAbsence && (
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition" onClick={(ev) => ev.stopPropagation()}>
                    {!e.is_break && <button onClick={() => setModal({ entry: e })} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700" data-testid={`ts-edit-${e.id}`}><Pencil className="w-4 h-4" /></button>}
                    <button onClick={() => doCopy(e.date)} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700" title={L.copy_next} data-testid={`ts-copy-${e.id}`}><Copy className="w-4 h-4" /></button>
                    <button onClick={() => doDelete(e)} className="p-1.5 rounded-lg text-rose-400 hover:bg-rose-50 hover:text-rose-600" data-testid={`ts-delete-${e.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                )}
              </div>
            );
          })}

          {/* Break / paid-break card */}
          {data?.day && (data.entries || []).length > 0 && data.day.break_status !== "none" && (
            <div className="flex items-center gap-2 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-2.5">
              {data.day.break_status === "paid" ? <CheckCircle2 className="w-4.5 h-4.5 text-emerald-500" /> : <Coffee className="w-4.5 h-4.5 text-slate-400" />}
              <span className="flex-1 text-sm font-semibold text-slate-600">
                {data.day.break_status === "paid" ? `${L.break_paid}${data.day.break_code ? ` · ${data.day.break_code}` : ""}` : L.break_deducted}
              </span>
              {!locked && data.day.break_status === "deducted" ? (
                <button onClick={() => setCodeOpen(true)} className="text-sm font-black text-cyan-700 hover:underline" data-testid="ts-have-code">{L.have_code}</button>
              ) : !locked && data.day.paid_break ? (
                <button onClick={clearCode} className="text-sm font-black text-rose-500 hover:underline" data-testid="ts-remove-code">{L.remove_code}</button>
              ) : null}
            </div>
          )}
        </div>
      )}

      {/* Add button */}
      {!locked && (
        <button onClick={() => setModal({ entry: null })} className="fixed bottom-6 right-6 sm:static sm:mt-4 sm:w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full sm:rounded-xl bg-cyan-500 hover:bg-cyan-600 text-white font-black shadow-lg shadow-cyan-500/30" data-testid="ts-add-fab">
          <Plus className="w-5 h-5" /> <span className="hidden sm:inline">{L.add}</span>
        </button>
      )}

      {modal && (
        <EntryModal L={L} date={selected} entry={modal.entry} projects={projects} activities={activities} defaultFrom={lastEnd}
          authHeader={authHeader} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}
      {codeOpen && <BreakCodeModal L={L} date={selected} authHeader={authHeader} onClose={() => setCodeOpen(false)} onApplied={() => { setCodeOpen(false); load(); }} />}
      {monthOpen && <MonthModal L={L} lang={lang} initialMonth={selected.slice(0, 7)} authHeader={authHeader} onClose={() => setMonthOpen(false)} />}
      {ferieOpen && <FerieModal L={L} isAdmin={isAdmin} authHeader={authHeader} onClose={() => { setFerieOpen(false); load(); }} />}
      {notifOpen && <NotifModal L={L} notifs={notifs} onClose={() => setNotifOpen(false)} />}
    </div>
  );
}

function Toggle({ on, onColor, onClick, testid }) {
  return (
    <button onClick={onClick} className="relative w-11 h-6 rounded-full transition" style={{ backgroundColor: on ? onColor : "#cbd5e1" }} data-testid={testid}>
      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
    </button>
  );
}

function getWeekNr(d) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - dayNum + 3);
  const firstThursday = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
  const diff = (date - firstThursday) / 86400000;
  return 1 + Math.round((diff - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
}

export default function DoserneTimesheet() {
  const { lang } = useLang();
  const L = DICT[lang] || DICT.ro;
  return (
    <DoserneShell title={L.title} subtitle={L.subtitle}>
      <TimesheetInner />
    </DoserneShell>
  );
}
